import { useState, useEffect, useMemo, useCallback } from 'react'
import { Search, Download, Server, Info, X, Settings2, Zap, Clock, Loader2, Package } from 'lucide-react'
import { Link } from 'react-router-dom'
import { gamesAPI } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import usePageTitle from '@/lib/usePageTitle'

/* Games-library — ETI-stijl grid + detail-paneel, in onze lanserver-look.
   Scan-service vult de DB automatisch vanaf `game_files/`. Deze pagina is
   read-only voor deelnemers; admin-beheer zit op /admin/games (aparte pagina). */

const CATEGORIES = [
  { id: '',            label: 'Alle',           color: '#B4FF39' },
  { id: 'FPS/Action',  label: 'FPS / Action',   color: '#FF7A6C' },
  { id: 'RTS',         label: 'RTS',            color: '#B892FF' },
  { id: 'Racing',      label: 'Racing',         color: '#FFB57A' },
  { id: 'Party',       label: 'Party / Coop',   color: '#7BCFFF' },
  { id: 'Sandbox',     label: 'Sandbox',        color: '#61E294' },
  { id: 'Andere',      label: 'Andere',         color: '#8892a5' },
]

const catColor = (cat) => CATEGORIES.find(c => c.id === cat)?.color || '#8892a5'
const gb = (bytes) => bytes ? (Number(bytes) / 1e9).toFixed(1) : '—'

export default function GamesLibraryPage() {
  usePageTitle('Games')
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin'
  const [games, setGames] = useState([])
  const [counts, setCounts] = useState({})
  const [cat, setCat] = useState('')
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  // Global Layout knijpt content in tot maxWidth:1180px + padding. Voor deze
  // library willen we volledige viewport-breedte. Body-class + scoped CSS
  // override de wrapper zonder Layout.jsx te wijzigen.
  useEffect(() => {
    document.body.classList.add('games-fullwidth')
    return () => document.body.classList.remove('games-fullwidth')
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (cat) params.category = cat
      if (q)   params.q = q
      const { data } = await gamesAPI.list(params)
      setGames(data.games || [])
      setCounts(data.counts || {})
      setErr(null)
      // Auto-select eerste game als selectie leeg is of niet meer in de lijst
      setSelected(prev => {
        if (prev && data.games.some(g => g.id === prev.id)) return prev
        return data.games[0] || null
      })
    } catch (e) {
      setErr(e.response?.data?.error || e.message)
    } finally {
      setLoading(false)
    }
  }, [cat, q])

  useEffect(() => { load() }, [load])

  const totalSizeGb = useMemo(
    () => games.reduce((n, g) => n + Number(g.size_bytes || 0), 0) / 1e9,
    [games])

  return (
    <div className="min-h-full bg-background text-foreground">
      <div className="grid grid-cols-[220px_1fr] gap-0">

        {/* Sidebar — sticky zodat je 'm altijd ziet bij scrollen */}
        <aside className="bg-card border-r border-border sticky top-0 self-start h-screen">
          <nav className="py-2">
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-5 pt-3 pb-2">
              Categorie
            </div>
            {CATEGORIES.map(c => (
              <button key={c.id}
                onClick={() => setCat(c.id)}
                className={`w-full flex items-center justify-between px-5 py-2 text-sm transition-colors border-l-[3px] ${
                  cat === c.id ? 'border-acid bg-accent' : 'border-transparent hover:bg-accent'
                }`}
                style={{ color: cat === c.id ? c.color : undefined }}>
                <span>{c.label}</span>
                <span className="font-mono text-[11px] text-muted-foreground tabular-nums">
                  {c.id ? (counts[c.id] || 0) : Object.values(counts).reduce((a,b)=>a+b, 0)}
                </span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main */}
        <main className="flex flex-col min-w-0">
          {/* Toolbar — sticky bovenaan */}
          <div className="sticky top-0 z-10 flex items-center gap-3 px-6 py-3 border-b border-border bg-background">
            <label className="flex-1 max-w-md flex items-center gap-2 bg-card border border-border rounded px-3 focus-within:border-acid transition-colors">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="search"
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Zoek game…"
                className="flex-1 bg-transparent border-none text-sm py-2 outline-none placeholder:text-muted-foreground"
              />
            </label>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground tabular-nums">
              {games.length} games · {totalSizeGb.toFixed(1)} GB
            </span>
            {isAdmin && (
              <Link to="/admin/games"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded border border-border text-xs uppercase tracking-wider font-mono text-muted-foreground hover:text-acid hover:border-acid transition-colors">
                <Settings2 className="h-3.5 w-3.5" /> Beheer
              </Link>
            )}
          </div>

          {/* Grid + Detail — links scrollt met page, rechts is sticky */}
          <div className="grid grid-cols-[minmax(0,1fr)_620px]">
            <div className="p-5">
              {err && <div className="text-signal text-sm mb-3">Kon niet laden: {err}</div>}
              {loading && <div className="text-muted-foreground text-sm">Laden…</div>}
              {!loading && games.length === 0 && (
                <div className="text-center py-16 text-muted-foreground">
                  <Info className="h-8 w-8 mx-auto mb-3 opacity-40" />
                  <div className="text-sm">Nog geen games in de library.</div>
                  <div className="text-xs mt-1">Admin: voeg game-folders toe aan <code className="font-mono text-acid">game_files/</code> op TrueNAS.</div>
                </div>
              )}
              <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-3">
                {games.map(g => (
                  <GameTile key={g.id}
                    game={g}
                    selected={selected?.id === g.id}
                    onClick={() => setSelected(g)} />
                ))}
              </div>
            </div>

            {selected
              ? <GameDetail key={selected.slug} game={selected} onClose={() => setSelected(null)} />
              : <div className="sticky top-[57px] self-start border-l border-border bg-card grid place-items-center text-muted-foreground text-sm p-8 text-center"
                     style={{ height: 'calc(100vh - 57px)' }}>
                  Kies een game links om details te zien
                </div>}
          </div>
        </main>
      </div>
    </div>
  )
}

/* ── Tile in de grid ────────────────────────────────────────────── */
function GameTile({ game, selected, onClick }) {
  const cc = catColor(game.category)
  const hasServer = game.has_server && !game.server_hidden
  return (
    <button onClick={onClick}
      className={`text-left bg-card border rounded overflow-hidden flex flex-col transition-all ${
        selected ? 'border-acid shadow-[0_0_0_1px_var(--acid)]' : 'border-border hover:border-muted-foreground hover:-translate-y-0.5'
      }`}>
      <div className="aspect-video bg-secondary relative overflow-hidden"
           style={{ background: game.poster_url ? `url(${game.poster_url}) center/cover` : `linear-gradient(135deg, ${cc}22, ${cc}55)` }}>
        {!game.poster_url && (
          <div className="absolute inset-0 grid place-items-center font-[Rajdhani] uppercase text-xs text-muted-foreground tracking-widest p-3 text-center">
            {game.name}
          </div>
        )}
        {hasServer && (
          <div className="absolute top-1.5 right-1.5 bg-background/85 backdrop-blur px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest text-live flex items-center gap-1">
            <span className="w-1 h-1 rounded-full bg-live" /> Server
          </div>
        )}
      </div>
      <div className="p-2.5">
        <div className="font-[Rajdhani] font-semibold text-[15px] leading-tight text-foreground">{game.name}</div>
        <div className="flex items-center gap-1.5 mt-1 font-mono text-[10px] uppercase tracking-wider" style={{ color: cc }}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: cc }} />
          {game.category} {game.release_year ? `· ${game.release_year}` : ''}
        </div>
      </div>
    </button>
  )
}

/* ── Detail-paneel rechts ───────────────────────────────────────── */
function GameDetail({ game, onClose }) {
  const cc = catColor(game.category)
  const hasServer = game.has_server && !game.server_hidden
  // Voorkeur voor visueel materiaal: screenshot > video > poster > gradient.
  const heroImage = game.screenshots?.[0] || game.poster_url
  const isYoutube  = game.video_url && /youtube\.com\/embed/.test(game.video_url)
  // Sta ook `?v=<mtime>` cache-buster toe achter .mp4 zodat een nieuw
  // gedownloade trailer niet als YouTube-embed geïnterpreteerd wordt.
  const isLocalMp4 = game.video_url && /^\/uploads\/.+\.mp4(\?|$)/.test(game.video_url)

  // ── Download-flow met ZIP-cache (spiegelt /nas-games) ────────────
  const [cacheStatus,    setCacheStatus]    = useState(null)  // { status, size? }
  const [downloading,    setDownloading]    = useState(false)
  // (dl-progress state is verwijderd — native browser-download stream toont
  // z'n eigen progress in de browser download-manager, veel efficiënter dan
  // chunks in JS-memory bufferen voor een custom bar.)
  const [dlError,        setDlError]        = useState(null)
  const [precaching,     setPrecaching]     = useState(false)

  const laadStatus = useCallback(async () => {
    try {
      const r = await gamesAPI.downloadStatus(game.slug)
      setCacheStatus(r.data)
      return r.data
    } catch { setCacheStatus({ status: 'none' }); return { status: 'none' } }
  }, [game.slug])

  useEffect(() => { laadStatus() }, [laadStatus])

  // Als er een pre-cache draait → poll elke 2s zodat de progress-bar
  // vlot mee updatet (het NAS-script schrijft `<zip>.tmp` in stukjes).
  useEffect(() => {
    if (cacheStatus?.status !== 'generating') return
    const iv = setInterval(laadStatus, 2000)
    return () => clearInterval(iv)
  }, [cacheStatus?.status, laadStatus])

  const startDownload = async () => {
    setDlError(null)
    // Cold hit → eerst een pre-cache job starten en de progress-bar voor
    // de ZIP-build volgen, i.p.v. een lang-lopende GET waar je niks van
    // ziet. Zodra `cached`, doen we de eigenlijke fetch-download.
    if (cacheStatus?.status !== 'cached') {
      try {
        if (cacheStatus?.status !== 'generating') {
          await gamesAPI.precache(game.slug)
        }
        let s = await laadStatus()
        while (s?.status === 'generating') {
          await new Promise(r => setTimeout(r, 2000))
          s = await laadStatus()
        }
        if (s?.status !== 'cached') {
          setDlError('ZIP-build gefaald — kijk in de admin/logs')
          return
        }
      } catch (e) {
        setDlError('Pre-cache mislukt: ' + (e.response?.data?.error || e.message))
        return
      }
    }

    // Native browser-download: <a download> streamt direct naar disk zonder
    // dat we chunks in JS-memory bufferen. Bij een 5 GB game bleef vroeger
    // de volledige zip in een Blob staan → out-of-memory in de tab. Progress
    // van deze fase toont de browser zelf in de download-manager. De echte
    // wacht — de ZIP-build op de NAS — heeft z'n eigen progress-bar
    // hierboven, dat is de stap waar de user écht op wacht.
    setDownloading(true)
    try {
      const a = document.createElement('a')
      a.href = `/api/games/${game.slug}/download`
      a.download = `${game.name}.zip`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      // Geen readable stream om te consumeren; korte wait zodat de UI
      // "downloaden…" nog even zichtbaar blijft, dan status refreshen.
      await new Promise(r => setTimeout(r, 1500))
      laadStatus()
    } catch (e) {
      setDlError(`Download mislukt: ${e.message}`)
    } finally {
      setDownloading(false)
    }
  }

  const startPrecache = async () => {
    setPrecaching(true)
    try {
      await gamesAPI.precache(game.slug)
      await laadStatus()
    } catch (e) {
      alert('Pre-cache starten mislukt: ' + (e.response?.data?.error || e.message))
    } finally { setPrecaching(false) }
  }

  return (
    // Sticky — blijft in view terwijl je door de linker grid scrollt.
    // Eigen scroll IN het paneel als content langer is dan viewport.
    // top-[57px] = onder de sticky toolbar.
    <div className="sticky top-[57px] self-start border-l border-border bg-background flex flex-col overflow-hidden"
         style={{ height: 'calc(100vh - 57px)' }}>
      {/* Scrollende inhoud (hero + body + meta) */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {/* Hero — met blur-behind zodat portret-covers niet gestretcht worden */}
        <div className="relative aspect-video flex items-end p-6 overflow-hidden bg-card">
          {isLocalMp4 && (
            <video
              key={game.video_url}
              src={game.video_url}
              autoPlay muted loop playsInline
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            />
          )}
          {isYoutube && !isLocalMp4 && (
            <iframe
              src={`${game.video_url}?autoplay=1&mute=1&loop=1&controls=0&modestbranding=1&playsinline=1&rel=0&playlist=${game.video_url.split('/').pop()}`}
              className="absolute inset-0 w-full h-full pointer-events-none"
              style={{ border: 0, transform: 'scale(1.4)' }}
              allow="autoplay; encrypted-media"
            />
          )}
          {!isLocalMp4 && !isYoutube && heroImage && (
            <>
              {/* blur-fill achter, contain vooraan — zo blijven portrait covers scherp */}
              <div className="absolute inset-0"
                   style={{ background: `url(${heroImage}) center/cover`, filter: 'blur(24px) brightness(0.6)', transform: 'scale(1.1)' }} />
              <img src={heroImage} alt="" className="absolute inset-0 w-full h-full object-contain" />
            </>
          )}
          {!heroImage && !isYoutube && !isLocalMp4 && (
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${cc}44, ${cc}77)` }} />
          )}
          {/* Overlay voor tekst-leesbaarheid */}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

          <button onClick={onClose}
                  className="absolute top-3 right-3 p-1.5 rounded bg-background/70 backdrop-blur hover:bg-background z-10">
            <X className="h-4 w-4" />
          </button>
          {(isYoutube || isLocalMp4) && (
            <div className="absolute top-3 left-3 bg-background/70 backdrop-blur px-2 py-1 rounded-sm text-[10px] font-mono uppercase tracking-widest text-acid flex items-center gap-1.5 z-10">
              <span className="w-1.5 h-1.5 rounded-full bg-acid animate-pulse" /> Preview
            </div>
          )}
          <div className="relative z-10">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-acid mb-1.5">
              {game.genre || game.category} {game.release_year ? `· ${game.release_year}` : ''}
            </div>
            <h1 className="font-[Rajdhani] font-bold text-[30px] leading-none uppercase text-foreground m-0"
                style={{ textShadow: '0 2px 20px rgba(0,0,0,0.9)' }}>
              {game.name}
            </h1>
          </div>
        </div>

      {/* Body */}
      <div className="px-6 pt-5">
        {game.description
          ? <p className="text-sm leading-relaxed max-w-[62ch]">{game.description}</p>
          : <p className="text-sm text-muted-foreground italic">Geen beschrijving beschikbaar — admin kan er een toevoegen via IGDB of manueel.</p>}

        <h3 className="font-[Rajdhani] font-bold text-[11px] uppercase tracking-widest text-muted-foreground mt-6 mb-2.5 pb-1.5 border-b border-border">
          Hoe opstarten
        </h3>
        <div className="bg-card border border-border border-l-[3px] border-l-acid rounded p-3.5 text-sm leading-relaxed whitespace-pre-line">
          {game.install_help || `1. Dubbelklik ${game.launch_cmd || 'game_start.cmd'} in de game-folder — geen installatie nodig.
2. Hoofdmenu → "Local Area Network" / "Multiplayer" → join de host op zijn LAN-IP.${hasServer ? `
3. Dedicated server: start ${game.server_cmd} op één toestel; anderen joinen op 192.168.129.x.` : ''}`}
        </div>

        <h3 className="font-[Rajdhani] font-bold text-[11px] uppercase tracking-widest text-muted-foreground mt-6 mb-2.5 pb-1.5 border-b border-border">
          Details
        </h3>
      </div>

      {/* Meta-strip */}
      <div className="px-6 pb-5 grid grid-cols-3 sm:grid-cols-6 gap-2">
        <MetaCell label="Multiplayer" value={game.max_players > 1 ? `${game.min_players || 1}–${game.max_players}` : 'Solo'} />
        <MetaCell label="Grootte" value={<>{gb(game.size_bytes)}<small className="text-muted-foreground uppercase text-[10px] font-mono ml-1">GB</small></>} />
        <MetaCell label="Genre" value={game.category} small />
        <MetaCell label="Release" value={game.release_year || '—'} />
        <MetaCell label="Publisher" value={game.publisher || '—'} small />
        <MetaCell label="Server" value={hasServer ? 'Ja' : 'Nee'} highlight={hasServer} />
      </div>

      </div>{/* /scroll-container */}

      {/* Actions — buiten scroll, altijd zichtbaar onderaan het paneel.
          ZIP wordt on-demand op de NAS gegenereerd + gecached; opnieuw
          downloaden komt uit cache = direct starten. */}
      <div className="shrink-0 px-6 py-3.5 bg-card border-t border-border space-y-2">
        {dlError && <div className="text-signal text-xs">{dlError}</div>}

        {/* ZIP-build progress — verschijnt zolang de NAS de `.tmp` file
            aan het schrijven is. Percent is `bytes_written / size_bytes`. */}
        {cacheStatus?.status === 'generating' && !downloading && (
          <div className="space-y-1">
            <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
              {cacheStatus.percent == null
                ? <div className="h-full rounded-full bg-yellow-500/70 animate-pulse" />
                : <div className="h-full rounded-full bg-yellow-500 transition-all duration-200" style={{ width: `${cacheStatus.percent}%` }} />}
            </div>
            <div className="flex justify-between text-[11px] text-muted-foreground font-mono tabular-nums">
              <span>ZIP wordt gebouwd op NAS{cacheStatus.percent != null ? ` · ${cacheStatus.percent}%` : '…'}</span>
              <span>
                {cacheStatus.bytes_total
                  ? `${((cacheStatus.bytes_written||0)/1e9).toFixed(2)} / ${(cacheStatus.bytes_total/1e9).toFixed(2)} GB`
                  : `${((cacheStatus.bytes_written||0)/1e6).toFixed(1)} MB`}
              </span>
            </div>
          </div>
        )}

        {/* Native browser-download indicator — geen bytes-counter meer, de
            browser toont z'n eigen progress. We tonen kort "gestart" tijdens
            de a.click() zodat de knop-state duidelijk is. */}
        {downloading && (
          <div className="text-[11px] text-muted-foreground font-mono">
            Download gestart — check je browser download-bar
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={startDownload}
            disabled={downloading || cacheStatus?.status === 'generating'}
            className="bg-acid hover:bg-acid/90 text-background font-[Rajdhani] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded flex items-center gap-1.5 disabled:opacity-50">
            {downloading
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Downloaden…</>
              : <><Download className="h-3.5 w-3.5" /> Download game (ZIP)</>}
          </button>

          {/* Pre-cache knop — alleen als nog niet cached én niet al bezig */}
          {cacheStatus?.status === 'none' && !downloading && (
            <button
              onClick={startPrecache}
              disabled={precaching}
              className="bg-transparent hover:bg-accent text-muted-foreground hover:text-foreground border border-border text-[11px] uppercase tracking-wider font-mono px-3 py-2 rounded flex items-center gap-1.5 disabled:opacity-50"
              title="ZIP nu al op de NAS laten voorbereiden zodat de download later direct start">
              {precaching ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
              Pre-cachen
            </button>
          )}

          {/* Cache-status badge */}
          {cacheStatus?.status === 'cached' && (
            <span className="text-[11px] font-mono text-live inline-flex items-center gap-1">
              <Zap className="h-3 w-3" /> gecached · {(cacheStatus.size/1e9).toFixed(2)} GB
            </span>
          )}
          {cacheStatus?.status === 'none' && !downloading && !precaching && (
            <span className="text-[11px] font-mono text-muted-foreground inline-flex items-center gap-1">
              <Clock className="h-3 w-3" /> eerste download bouwt de ZIP
            </span>
          )}

          {hasServer && (
            <button
              onClick={() => { const p = `${game.folder_path}\\${game.server_cmd}`; navigator.clipboard?.writeText(p); alert(`Server-pad gekopieerd:\n${p}`) }}
              className="ml-auto bg-transparent hover:bg-accent text-muted-foreground hover:text-foreground p-2 rounded border border-border"
              title={`Server starten (${game.server_cmd})`}>
              <Server className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function MetaCell({ label, value, small = false, highlight = false }) {
  return (
    <div className={`p-2.5 bg-card border rounded flex flex-col gap-1 ${highlight ? 'border-live/40 bg-live/5' : 'border-border'}`}>
      <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground font-medium">{label}</span>
      <span className={`font-[Rajdhani] font-semibold leading-none tabular-nums ${small ? 'text-[13px]' : 'text-[16px]'} ${highlight ? 'text-live' : 'text-foreground'}`}>
        {value}
      </span>
    </div>
  )
}
